import React, { useContext } from "react";
import { Link } from "react-router-dom";
import { ApplicationContext } from "../../context/GlobalState";
import { t } from "../../i18n";
import { FaPhone, FaCalendarCheck } from "react-icons/fa";
import gaHero from "../../assets/images/heroes/ga-hero-main.webp";
import ncHero from "../../assets/images/heroes/nc-hero-main.webp";
import "../../containers/Home/Home.css";

// perf(homepage-lcp) 2026-08-31: PUBLIC paths PRIMARY, webpack-hashed FALLBACK
// (matches Hero.js). Homepage was stuck at LCP 4.1-4.4s mobile lab even after
// the 2026-08-24 route-page fix because the preload target (/static/media/
// nc-hero-main.HASH.webp), the static-hero-shell CSS background, and the
// React-mounted section.home-hero all referenced the SAME hashed webpack path.
// The static shell painted fast (observed LCP 908ms) but the actual LCP metric
// tracks section.home-hero (larger element), which only appears after React
// hydrates -> lcpLoadDelay ~1.4s + lcpLoadDuration ~2.5s on throttled mobile.
//
// The LCP-winning pages (/*/car-accident-doctor/, /blog/*) all preload the
// PUBLIC /assets/images/heroes/*.webp path and paint the same public asset;
// aligning the homepage on that pattern matches what already worked.
// public/assets/images/heroes/car-crash-hero.webp is byte-identical to
// src/assets/images/heroes/ga-hero-main.webp; same for apex-hero.webp /
// nc-hero-main.webp.
const BRAND_HEROES = {
  gso: "/assets/images/heroes/car-crash-hero.webp",
  "apex-nc": "/assets/images/heroes/apex-hero.webp",
  "apex-tn": "/assets/images/heroes/apex-hero.webp",
};
const BRAND_HEROES_FALLBACK = {
  gso: gaHero,
  "apex-nc": ncHero,
  "apex-tn": ncHero,
};

/**
 * The homepage hero, extracted from containers/Home/Home.js so it can ALSO be
 * rendered as the Suspense fallback for the lazy-loaded Home route.
 *
 * Why: Home is a lazy chunk. On first load React mounts, CLOBBERS the static
 * hero shell shipped in index.html, and then showed an empty 100vh div while
 * the Home chunk downloaded — a visible hero -> blank -> hero "jump" (client
 * report 2026-07-10). Rendering the real hero as the fallback makes the
 * fallback and the mounted Home paint identical above-the-fold pixels, so the
 * chunk swap is invisible.
 *
 * Must stay visually in lock-step with the static shell emitted by
 * scripts/postbuild-brand-og.js (heroSkeleton).
 */
export default function HomeHero() {
  const { state } = useContext(ApplicationContext);
  const brandKey = state.brand?.brandKey || "gso";
  const locale = state.locale || "en";
  const prefix = locale === "en" ? "" : `/${locale}`;
  const brand = state.brand || state.client;
  const heroImg = BRAND_HEROES[brandKey] || BRAND_HEROES.gso;
  const fallbackImg = BRAND_HEROES_FALLBACK[brandKey] || gaHero;

  return (
    <section
      className="home-hero"
      style={{
        backgroundImage: `linear-gradient(var(--glass-hero-overlay, rgba(26,54,93,0.72)), var(--glass-hero-overlay, rgba(26,54,93,0.72))), url(${heroImg}), url(${fallbackImg})`,
        backgroundSize: "cover",
        backgroundPosition: "center"
      }}
    >
      <div className="container">
        <div className="home-hero-grid">
        <div className="home-hero-content">
          <h1>{brandKey === 'gso' && locale === 'en'
            ? 'Local Orthopedic, Spine & Injury Care Across Georgia'
            : t("home.heroTitle", state.locale)}</h1>
          <p>{t("site.tagline", state.locale)}</p>
          <div className="home-hero-actions">
            <Link to={`${prefix}/request-appt/`} className="hero-btn-primary"><FaCalendarCheck /> {t("cta.schedule", state.locale)}</Link>
            <a href={`tel:${brand?.phone || state.client.phone}`} className="hero-btn-secondary"><FaPhone /> {t('phone.newPatients', locale, { phone: brand?.phone || state.client.phone })}</a>
            {brand?.phoneExisting && (
              <a href={`tel:${brand.phoneExisting}`} className="hero-btn-secondary"><FaPhone /> {t('phone.existingPatients', locale, { phone: brand.phoneExisting })}</a>
            )}
          </div>
          <p className="home-hero-insurance-notice" style={{ marginTop: '1rem', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.92)', color: '#7a1f1f', borderLeft: '4px solid #c53030', fontWeight: 600, display: 'inline-block', borderRadius: 4 }}>
            {t('cta.insuranceNotice', locale)}
          </p>
        </div>
        <div className="home-hero-cutout" aria-hidden="true">
          {brandKey !== 'apex-tn' && (
            <img
              src={brandKey === 'gso'
                ? '/assets/brand/gso/hero/doctors-cutout.webp'
                : '/assets/brand/apex/hero/doctors-cutout.webp'}
              alt=""
              loading="eager"
              decoding="async"
            />
          )}
        </div>
        </div>
      </div>
    </section>
  );
}
